import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminApi, type BookingFeedbackDto } from '@/api/admin'
import { DateFilter, getRange, type Preset } from '@/features/admin/analytics/DateFilter'
import { LiveSessionsList, LiveSummaryStats } from '@/features/live/LiveSessionsList'
import { useTopBar } from '@/contexts/TopBarContext'
import { Star } from 'lucide-react'
import { useAdminDates } from '@/hooks/useDates'

function RatingStars({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 10 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          className={i < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
        />
      ))}
      <span className="ml-1.5 text-sm font-semibold text-gray-700">{rating}/10</span>
    </div>
  )
}

function FeedbackCard({ fb }: { fb: BookingFeedbackDto }) {
  const { formatDate } = useAdminDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', { day: 'numeric', month: 'short', year: 'numeric' })
  }
  const fmtDateTime = (d: string) => {
    return formatDate(d, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-2">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-semibold text-gray-900">{fb.courseName}</p>
          <p className="text-sm text-gray-500">
            {fb.clientName} · {fmtDate(fb.bookingDate)}
          </p>
        </div>
        <RatingStars rating={fb.rating} />
      </div>
      {fb.comment && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 whitespace-pre-wrap">
          {fb.comment}
        </p>
      )}
      <p className="text-xs text-gray-400">
        Submitted {fmtDateTime(fb.submittedAt)}
        {fb.updatedAt && <span> · Edited {fmtDateTime(fb.updatedAt)}</span>}
      </p>
    </div>
  )
}

/** Attendee feedback, learning gain and confidence change from ended CertSlate Live sessions. */
function LiveSessionsTab() {
  const { formatDate } = useAdminDates()
  const [preset, setPreset] = useState<Preset>('12m')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [courseId, setCourseId] = useState('')
  const { from, to } = getRange(preset, customFrom, customTo)

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['live-insights', from, to, courseId],
    queryFn: () => adminApi.getLiveInsights(from, to, courseId || null).then(r => r.data),
  })

  const fmtDate = (d: string) => formatDate(d + 'T00:00:00', { day: 'numeric', month: 'short', year: 'numeric' })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <DateFilter preset={preset} setPreset={setPreset} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} />
        <select
          value={courseId}
          onChange={e => setCourseId(e.target.value)}
          className="h-9 rounded-md border border-input bg-white px-3 text-sm shadow-sm"
          aria-label="Course"
        >
          <option value="">All courses</option>
          {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <p className="text-xs text-gray-400">Anonymous feedback from attendees in live sessions. Sessions with fewer than 3 responses are marked as a small sample.</p>

      <LiveSummaryStats sessions={sessions} />

      {isLoading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : sessions.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm bg-white border border-gray-200 rounded-xl">
          <p>No live sessions in this period.</p>
          <p className="text-xs mt-1">Results appear here once a live session has ended.</p>
        </div>
      ) : (
        <LiveSessionsList sessions={sessions} formatDate={fmtDate} />
      )}
    </div>
  )
}

export default function FeedbackPage() {
  useTopBar(<span className="text-sm font-medium text-gray-900">Feedback</span>)
  const [tab, setTab] = useState<'client' | 'live'>('client')
  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const { data: feedbacks = [], isLoading } = useQuery({
    queryKey: ['admin-feedback'],
    queryFn: () => adminApi.getAllFeedback().then(r => r.data),
  })

  const avgRating = feedbacks.length > 0
    ? (feedbacks.reduce((s, f) => s + f.rating, 0) / feedbacks.length).toFixed(1)
    : null

  return (
    <div className="space-y-6">
      {profile?.liveDeliveryEnabled && (
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {([['client', 'Client ratings'], ['live', 'Live sessions']] as const).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setTab(value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${tab === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {profile?.liveDeliveryEnabled && tab === 'live' ? <LiveSessionsTab /> : <>
      {/* Summary */}
      {feedbacks.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm text-center">
            <p className="text-3xl font-bold text-amber-500">{avgRating}</p>
            <p className="text-xs text-gray-500 mt-1">Average rating</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm text-center">
            <p className="text-3xl font-bold text-gray-900">{feedbacks.length}</p>
            <p className="text-xs text-gray-500 mt-1">Total responses</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm text-center col-span-2 sm:col-span-1">
            <p className="text-3xl font-bold text-green-600">
              {feedbacks.filter(f => f.rating >= 8).length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Rated 8+/10</p>
          </div>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : feedbacks.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm bg-white border border-gray-200 rounded-xl">
          <Star size={32} className="mx-auto text-gray-200 mb-3" />
          <p>No feedback submitted yet.</p>
          <p className="text-xs mt-1">Feedback appears here once clients rate their completed bookings.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {feedbacks.map(fb => <FeedbackCard key={fb.id} fb={fb} />)}
        </div>
      )}
      </>}
    </div>
  )
}
